import React, { useState, useEffect } from 'react';
import axios from 'axios';
import male from '../assets/images/male.png';
import female from '../assets/images/female.png';
import TestimonialForm from './TestimonialForm';
import Footer from './Footer';
import Reveal from './Reveal';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const timeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  let interval = Math.floor(seconds / 31536000);
  if (interval >= 1) return `${interval} year${interval !== 1 ? 's' : ''} ago`;
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) return `${interval} month${interval !== 1 ? 's' : ''} ago`;
  interval = Math.floor(seconds / 604800);
  if (interval >= 1) return `${interval} week${interval !== 1 ? 's' : ''} ago`;
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) return `${interval} day${interval !== 1 ? 's' : ''} ago`;
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) return `${interval} hour${interval !== 1 ? 's' : ''} ago`;
  interval = Math.floor(seconds / 60);
  if (interval >= 1) return `${interval} minute${interval !== 1 ? 's' : ''} ago`;
  return 'just now';
};

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const QuoteMark = () => (
  <svg viewBox="0 0 32 24" width="30" height="22" fill="none" aria-hidden="true" className="text-[var(--accent,#7A1B2E)] opacity-80 mb-3">
    <path d="M0 24V14.4C0 6.4 4.8 1.2 12.8 0L14.4 3.2C9.6 4.8 7.2 7.6 7.2 11.2H12.8V24H0ZM19.2 24V14.4C19.2 6.4 24 1.2 32 0L33.6 3.2C28.8 4.8 26.4 7.6 26.4 11.2H32V24H19.2Z" fill="currentColor" />
  </svg>
);

const TestimonialCard = ({ testimonial, onClick }) => {
  const limitedText = testimonial.text.split(' ').slice(0, 20).join(' ') + (testimonial.text.split(' ').length > 20 ? '…' : '');

  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left w-full bg-white dark:bg-[#111010] border border-gray-200 dark:border-white/10
        rounded-2xl p-6 shadow-sm hover:shadow-lg dark:shadow-black/30 dark:hover:shadow-black/50
        transition-all duration-300 hover:-translate-y-1 flex flex-col"
    >
      <QuoteMark />
      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-5 flex-1">
        {limitedText}
      </p>
      <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-white/10">
        <span className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden border border-gray-200 dark:border-white/15 ${testimonial.isFallbackIcon ? 'bg-gray-100 dark:bg-white/10 p-2' : ''}`}>
          <img
            src={testimonial.profileImage}
            alt={`${testimonial.name}'s profile`}
            className={testimonial.isFallbackIcon ? 'w-full h-full object-contain dark:invert opacity-70' : 'w-full h-full object-cover'}
          />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{testimonial.name}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">{timeAgo(testimonial.date)}</p>
        </div>
        <div className="flex items-center gap-1 text-gray-400 dark:text-gray-500 text-xs flex-shrink-0">
          <EyeIcon />
          <span>{testimonial.views}</span>
        </div>
      </div>
    </button>
  );
};

const Testimonial = () => {
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [allTestimonials, setAllTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/testimonials/all`);
        const confirmedTestimonials = response.data.testimonials.filter(
          (testimonial) => testimonial.status.toLowerCase() === 'confirmed'
        );

        const formattedTestimonials = confirmedTestimonials.map((testimonial) => ({
          id: testimonial.ID,
          name: testimonial.names || 'Unlisted',
          text: testimonial.description,
          gender: testimonial.gender.toLowerCase(),
          profileImage: testimonial.image
            ? `${BASE_URL}${testimonial.image.replace('\\', '/')}`
            : (testimonial.gender.toLowerCase() === 'male' ? male : female),
          isFallbackIcon: !testimonial.image,
          date: testimonial.date,
          views: testimonial.views,
        }));

        setAllTestimonials(formattedTestimonials);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  const handleCardClick = async (testimonial) => {
    try {
      await axios.put(`${BASE_URL}/api/testimonials/increment-views/${testimonial.id}`);
      setAllTestimonials((prev) =>
        prev.map((t) => (t.id === testimonial.id ? { ...t, views: t.views + 1 } : t))
      );
    } catch (error) {
      console.error('Error updating testimonial views:', error);
    }
    setSelectedTestimonial(testimonial);
  };

  const handleFormSubmit = (newTestimonial) => {
    setAllTestimonials((prev) => [
      ...prev,
      {
        ...newTestimonial,
        id: prev.length + 1,
        profileImage: newTestimonial.gender === 'male' ? male : female,
        isFallbackIcon: true,
        date: new Date().toISOString(),
        views: 0,
      },
    ]);
  };

  return (
    <div className="bg-white dark:bg-black min-h-screen transition-colors duration-300">
      <div className="container mx-auto px-4 pt-[140px] pb-20 max-w-6xl">
        <Reveal className="text-center mb-10">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 dark:text-gray-500 mb-2">Testimonies</p>
          <h1 className="font-glsnecb text-5xl md:text-7xl uppercase text-gray-900 dark:text-white">Wall of Praise</h1>
        </Reveal>

        <div className="flex justify-center mb-10">
          <button
            onClick={() => setIsFormOpen(true)}
            className="flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-black
              text-sm font-medium px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity"
          >
            <span className="text-lg leading-none">+</span> Share your testimony
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-gray-100 dark:bg-white/5 rounded-2xl p-6 h-48 animate-pulse" />
            ))}
          </div>
        ) : allTestimonials.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400">No testimonies yet. Be the first to share one.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {allTestimonials
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .map((testimonial, i) => (
                <Reveal key={testimonial.id} delay={Math.min(i, 8) * 60}>
                  <TestimonialCard testimonial={testimonial} onClick={() => handleCardClick(testimonial)} />
                </Reveal>
              ))}
          </div>
        )}

        {selectedTestimonial && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/70 z-50 p-4"
            onClick={() => setSelectedTestimonial(null)}
          >
            <div
              className="bg-white dark:bg-[#111010] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white
                p-8 rounded-2xl relative max-w-lg w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                onClick={() => setSelectedTestimonial(null)}
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              <div className="flex flex-col items-center text-center">
                <span className={`w-20 h-20 mb-4 rounded-full flex items-center justify-center overflow-hidden border border-gray-200 dark:border-white/15 ${selectedTestimonial.isFallbackIcon ? 'bg-gray-100 dark:bg-white/10 p-4' : ''}`}>
                  <img
                    src={selectedTestimonial.profileImage}
                    alt={`${selectedTestimonial.name}'s profile`}
                    className={selectedTestimonial.isFallbackIcon ? 'w-full h-full object-contain dark:invert opacity-70' : 'w-full h-full object-cover'}
                  />
                </span>
                <h2 className="text-xl font-semibold mb-2">{selectedTestimonial.name}</h2>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{selectedTestimonial.text}</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-4">{timeAgo(selectedTestimonial.date)}</p>
                <div className="flex items-center gap-1 text-gray-400 dark:text-gray-500 text-xs mt-2">
                  <EyeIcon />
                  <span>{selectedTestimonial.views}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {isFormOpen && (
          <TestimonialForm onSubmit={handleFormSubmit} onClose={() => setIsFormOpen(false)} />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Testimonial;
